import React, { useState, useMemo, useEffect } from 'react';
import { BarChart3, TrendingUp, Download, DollarSign, ShoppingCart, Package, Eye, X, Printer, Search, ChevronDown, ChevronUp, Tag, Star, FileText, Activity, Layers, Warehouse, CreditCard } from 'lucide-react';
import { fetchOrders as apiFetchOrders, fetchExpenses as apiFetchExpenses } from '../api';

const fmt = (n) => Math.round(n).toLocaleString('en-IN');

/* ── Invoice Modal ── */
const InvoiceModal = ({ order, onClose }) => {
  if (!order) return null;
  const sub = order.products?.reduce((s, p) => s + p.price * (p.quantity || 1), 0) || order.total;
  const tax = Math.round(sub * 0.05), grand = sub + tax;
  
  const print = () => {
    const html = `<html><head><title>Invoice</title><style>body{font-family:Arial;margin:30px;color:#111}.hdr{display:flex;justify-content:space-between;border-bottom:3px solid #16a34a;padding-bottom:20px;margin-bottom:20px}h1{color:#16a34a;margin:0}table{width:100%;border-collapse:collapse;margin-top:15px}th{background:#f0fdf4;color:#15803d;padding:10px;text-align:left;font-size:12px}td{padding:10px;border-bottom:1px solid #e5e7eb;font-size:12px}.tot{display:flex;justify-content:space-between;padding:4px 0;font-size:14px}.grand{font-size:18px;font-weight:bold;color:#16a34a;border-top:2px solid #16a34a;padding-top:8px;margin-top:8px}</style></head><body><div class="hdr"><div><h1>🌿 Manthuli Organic</h1><p>GST: 33AABCM1234A1Z5</p></div><div style="text-align:right"><h2>INVOICE #${order.id}</h2><p>${order.date} ${order.time || ''}</p></div></div><p><b>To:</b> ${order.customer} ${order.phone ? '| ' + order.phone : ''}</p><table><thead><tr><th>#</th><th>Product</th><th>Weight</th><th>Unit ₹</th><th>Qty</th><th>Total</th></tr></thead><tbody>${(order.products || []).map((p, i) => `<tr><td>${i + 1}</td><td>${p.name}</td><td>${p.weight} ${p.unit}</td><td>₹${p.price}</td><td>${p.quantity || 1}</td><td>₹${(p.price * (p.quantity || 1)).toLocaleString()}</td></tr>`).join('')}</tbody></table><div style="float:right;min-width:220px;margin-top:10px"><div class="tot"><span>Subtotal</span><span>₹${sub.toLocaleString()}</span></div><div class="tot"><span>GST 5%</span><span>₹${tax.toLocaleString()}</span></div><div class="tot grand"><span>Grand Total</span><span>₹${grand.toLocaleString()}</span></div></div><p style="text-align:center;color:#888;margin-top:60px;font-size:12px">Thank you for shopping with Manthuli Organic Shop!</p></body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    w.print();
  };

  const statusConfig = {
    completed: { text: 'Completed', bg: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
    paid: { text: 'Paid', bg: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
    pending: { text: 'Pending', bg: 'bg-amber-50 text-amber-700 border-amber-100' },
    processing: { text: 'Processing', bg: 'bg-blue-50 text-blue-700 border-blue-100' },
    shipped: { text: 'Shipped', bg: 'bg-indigo-50 text-indigo-700 border-indigo-100' },
    cancelled: { text: 'Cancelled', bg: 'bg-rose-50 text-rose-700 border-rose-100' }
  };

  const orderStatus = statusConfig[order.status?.toLowerCase()] || { text: order.status, bg: 'bg-slate-50 text-slate-700 border-slate-100' };
  const payStatus = statusConfig[order.paymentStatus?.toLowerCase()] || { text: order.paymentStatus || 'Pending', bg: 'bg-slate-50 text-slate-700 border-slate-100' };

  return (
    <div className="ui-modal-overlay">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-slate-100 animate-fade-in">
        {/* Modal Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-emerald-50/60 to-green-50/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-600 to-green-700 flex items-center justify-center shadow-lg shadow-emerald-600/20">
              <FileText size={18} color="#fff" />
            </div>
            <div>
              <p className="text-emerald-700 text-[10px] font-bold uppercase tracking-wider">Invoice Overview</p>
              <h2 className="text-slate-800 font-extrabold text-lg">Invoice #{order.id}</h2>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={print} className="ui-btn-primary !px-4 !py-2 !rounded-lg flex items-center gap-1.5 text-xs">
              <Printer size={14} />
              Print
            </button>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors border border-slate-200 bg-white">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="overflow-y-auto flex-1 p-6 ui-scrollbar">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-slate-50/60 border border-slate-100 rounded-2xl p-4">
              <p className="text-slate-400 text-[10px] font-bold uppercase tracking-wider mb-2">Bill To</p>
              <p className="text-slate-800 font-bold text-sm mb-1">{order.customer}</p>
              {order.phone && <p className="text-slate-500 text-xs mb-1">📞 {order.phone}</p>}
              {order.address && <p className="text-slate-500 text-xs leading-relaxed">{order.address}</p>}
            </div>
            <div className="bg-slate-50/60 border border-slate-100 rounded-2xl p-4 flex flex-col justify-between">
              <div>
                <p className="text-slate-400 text-[10px] font-bold uppercase tracking-wider mb-2">Order Status</p>
                <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold border ${orderStatus.bg}`}>
                  {orderStatus.text}
                </span>
              </div>
              <div className="mt-4">
                <p className="text-slate-400 text-[10px] font-bold uppercase tracking-wider mb-2">Payment</p>
                <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold border ${payStatus.bg}`}>
                  {payStatus.text}
                </span>
              </div>
            </div>
          </div>

          <div className="ui-table-wrap mb-6 border border-slate-100">
            <table className="ui-table">
              <thead>
                <tr>
                  <th className="ui-th !py-3 !px-4">#</th>
                  <th className="ui-th !py-3 !px-4">Product</th>
                  <th className="ui-th !py-3 !px-4 text-center">Weight</th>
                  <th className="ui-th !py-3 !px-4 text-right">Unit Price</th>
                  <th className="ui-th !py-3 !px-4 text-center">Qty</th>
                  <th className="ui-th !py-3 !px-4 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {(order.products || []).map((p, i) => (
                  <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                    <td className="ui-td !py-3 !px-4 text-slate-400 font-medium">{i + 1}</td>
                    <td className="ui-td !py-3 !px-4 text-slate-800 font-bold">{p.name}</td>
                    <td className="ui-td !py-3 !px-4 text-center text-slate-500 font-medium">{p.weight} {p.unit}</td>
                    <td className="ui-td !py-3 !px-4 text-right text-emerald-600 font-extrabold">₹{p.price?.toLocaleString()}</td>
                    <td className="ui-td !py-3 !px-4 text-center text-slate-600 font-bold">{p.quantity || 1}</td>
                    <td className="ui-td !py-3 !px-4 text-right text-slate-800 font-extrabold">₹{((p.price || 0) * (p.quantity || 1)).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end">
            <div className="w-full max-w-[280px] bg-slate-50/80 border border-slate-100 rounded-2xl p-4 space-y-2">
              <div className="flex justify-between text-xs text-slate-500">
                <span>Subtotal</span>
                <span className="font-semibold text-slate-700">₹{sub.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-xs text-slate-500">
                <span>GST (5%)</span>
                <span className="font-semibold text-slate-700">₹{tax.toLocaleString()}</span>
              </div>
              <div className="border-t border-emerald-500/25 my-2 pt-2 flex justify-between items-center">
                <span className="text-sm font-extrabold text-emerald-700">Grand Total</span>
                <span className="text-lg font-black text-emerald-600">₹{grand.toLocaleString()}</span>
              </div>
            </div>
          </div>
          
          <p className="text-center text-slate-400 text-xs mt-6 pt-4 border-t border-slate-100">
            🌿 Thank you for supporting organic farming with Manthuli Organic Shop!
          </p>
        </div>
      </div>
    </div>
  );
};

/* ── Product Detail Modal ── */
const ProductDetailModal = ({ product, allOrders, onClose, onViewInvoice }) => {
  if (!product) return null;
  const sr = product.sellingRevenue || 0, cr = product.costRevenue || 0;
  
  return (
    <div className="ui-modal-overlay">
      <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-slate-100 animate-fade-in">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-purple-50/60 to-indigo-50/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-700 flex items-center justify-center shadow-lg shadow-purple-600/20">
              <Package size={18} color="#fff" />
            </div>
            <div>
              <p className="text-purple-700 text-[10px] font-bold uppercase tracking-wider">Product Sales Insights</p>
              <h2 className="text-slate-800 font-extrabold text-lg">{product.name}</h2>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors border border-slate-200 bg-white">
            <X size={16} />
          </button>
        </div>

        {/* Top summary boxes */}
        <div className="grid grid-cols-2 md:grid-cols-5 border-b border-slate-100 bg-slate-50/30">
          {[
            { l: 'Units Sold', v: product.unitsSold, c: 'text-purple-700', bg: 'bg-purple-50/30' },
            { l: 'Selling Rev', v: `₹${fmt(sr)}`, c: 'text-emerald-700', bg: 'bg-emerald-50/30' },
            { l: 'Cost Rev', v: `₹${fmt(cr)}`, c: 'text-rose-700', bg: 'bg-rose-50/30' },
            { l: 'Profit', v: `₹${fmt(product.profit || 0)}`, c: 'text-amber-700', bg: 'bg-amber-50/30' },
            { l: 'Invoices', v: product.orders.length, c: 'text-blue-700', bg: 'bg-blue-50/30' }
          ].map((s, i) => (
            <div key={i} className={`p-4 text-center border-r border-slate-100 last:border-r-0 ${s.bg}`}>
              <p className="text-slate-400 text-[9px] font-bold uppercase tracking-wider mb-1">{s.l}</p>
              <p className={`font-black text-base md:text-lg ${s.c}`}>{s.v}</p>
            </div>
          ))}
        </div>

        {/* Detailed Table */}
        <div className="flex-1 overflow-y-auto ui-scrollbar p-6">
          <div className="ui-table-wrap border border-slate-100 shadow-sm">
            <table className="ui-table">
              <thead className="sticky top-0 bg-white z-10 shadow-[0_1px_0_0_#f1f5f9]">
                <tr>
                  <th className="ui-th !py-3">Invoice #</th>
                  <th className="ui-th !py-3">Date</th>
                  <th className="ui-th !py-3">Customer</th>
                  <th className="ui-th !py-3 text-center">Qty</th>
                  <th className="ui-th !py-3 text-right">Sell Price</th>
                  <th className="ui-th !py-3 text-right">Cost Price</th>
                  <th className="ui-th !py-3 text-right">Sell Total</th>
                  <th className="ui-th !py-3 text-right">Cost Total</th>
                  <th className="ui-th !py-3 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {product.orders.slice().sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)).map((o, idx) => {
                  const full = allOrders.find(x => x.id === o.orderId);
                  return (
                    <tr key={idx} className="hover:bg-purple-50/10 transition-colors">
                      <td className="ui-td !py-3 text-purple-700 font-extrabold">{o.invoiceNumber || o.orderId}</td>
                      <td className="ui-td !py-3 text-slate-500 font-medium whitespace-nowrap">
                        {o.date}
                        {o.time && <span className="text-slate-400 text-[10px]"> · {o.time}</span>}
                      </td>
                      <td className="ui-td !py-3 text-slate-800 font-bold">{o.customer || '—'}</td>
                      <td className="ui-td !py-3 text-center text-slate-700 font-black">{o.quantity}</td>
                      <td className="ui-td !py-3 text-right text-emerald-600 font-extrabold">₹{(o.sellingUnit ?? o.unitPrice ?? 0).toLocaleString()}</td>
                      <td className="ui-td !py-3 text-right text-rose-600 font-bold">₹{Math.round(o.costUnit ?? 0).toLocaleString()}</td>
                      <td className="ui-td !py-3 text-right text-emerald-700 font-black">₹{(o.total || 0).toLocaleString()}</td>
                      <td className="ui-td !py-3 text-right text-rose-700 font-bold">₹{Math.round(o.costTotal ?? 0).toLocaleString()}</td>
                      <td className="ui-td !py-3 text-center">
                        {full ? (
                          <button onClick={() => onViewInvoice(full)} className="ui-btn-primary !px-3 !py-1.5 !rounded-lg text-xs flex items-center justify-center gap-1 shadow-purple-200">
                            <Eye size={12} />
                            View
                          </button>
                        ) : (
                          <span className="text-slate-300 font-medium">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── MAIN ANALYTICS ── */
const Analytics = ({ products = [] }) => {
  const [liveOrders, setLiveOrders] = useState([]);
  const [liveExpenses, setLiveExpenses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    Promise.all([
      apiFetchOrders().catch((err) => {
        console.error('Analytics: failed to load orders', err);
        return [];
      }),
      apiFetchExpenses().catch((err) => {
        console.error('Analytics: failed to load expenses', err);
        return [];
      }),
    ])
      .then(([rawOrders, expenses]) => {
        if (cancelled) return;
        const formatted = (rawOrders || []).map(o => {
          const dateObj = new Date(o.order_date || o.created_at);
          const dd = String(dateObj.getDate()).padStart(2, '0');
          const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
          const yyyy = dateObj.getFullYear();
          return {
            id: `ORD${String(o.id).padStart(3, '0')}`,
            rawId: o.id,
            customer: o.customer_name || 'Guest',
            total: parseFloat(o.total_amount) || 0,
            status: o.status || 'pending',
            date: `${dd}.${mm}.${yyyy}`,
            time: dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
            paymentStatus: o.payment_status || 'pending',
            address: o.delivery_address,
            phone: o.phone || o.customer_phone,
            products: (o.items || []).map(item => ({
              name: item.item_name || 'Unknown Item',
              weight: item.product_weight || '-',
              unit: item.product_unit || '',
              price: parseFloat(item.item_price) || 0,
              quantity: item.quantity || 1,
            }))
          };
        });
        setLiveOrders(formatted);
        setLiveExpenses(Array.isArray(expenses) ? expenses : []);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const orders = liveOrders;
  
  const getCostPrice = (name, sp) => {
    const m = products?.find(p => p.name === name || p.name?.toLowerCase() === name?.toLowerCase());
    return (m?.castingPrice && Number(m.castingPrice) > 0) ? Number(m.castingPrice) : sp * 0.65;
  };

  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [timeFilter, setTimeFilter] = useState('all'); // 'today','week','month','year','custom','all'
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('sellingRevenue');
  const [sortDir, setSortDir] = useState('desc');
  const [selProduct, setSelProduct] = useState(null);
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const [selectedProductName, setSelectedProductName] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [selectedCity, setSelectedCity] = useState(null); // city drill-down modal
  const [cityInvoiceData, setCityInvoiceData] = useState(null); // invoice preview

  // Compute start/end dates based on time range filters
  const getPeriodRange = (filter) => {
    const now = new Date();
    const pad = n => String(n).padStart(2, '0');
    const fmt8 = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    
    if (filter === 'today') {
      const s = fmt8(now); return { start: s, end: s };
    }
    if (filter === 'week') {
      const day = now.getDay();
      const mon = new Date(now);
      mon.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
      return { start: fmt8(mon), end: fmt8(now) };
    }
    if (filter === 'month') {
      const s = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: fmt8(s), end: fmt8(now) };
    }
    if (filter === 'year') {
      const s = new Date(now.getFullYear(), 0, 1);
      return { start: fmt8(s), end: fmt8(now) };
    }
    return null;
  };

  const filteredOrders = useMemo(() => {
    const range = timeFilter !== 'custom' ? getPeriodRange(timeFilter) : dateRange;
    if (!range || (!range.start && !range.end)) return orders;
    return orders.filter(o => {
      const p = o.date?.split('.');
      if (!p || p.length < 3) return true;
      const [dd, mm, yyyy] = p;
      const d = new Date(`${yyyy}-${mm}-${dd}`);
      if (range.start && d < new Date(range.start)) return false;
      if (range.end && d > new Date(range.end)) return false;
      return true;
    });
  }, [orders, timeFilter, dateRange]);

  const filteredExpenses = useMemo(() => {
    const range = timeFilter !== 'custom' ? getPeriodRange(timeFilter) : dateRange;
    if (!range || (!range.start && !range.end)) return liveExpenses;
    return (liveExpenses || []).filter((e) => {
      const d = e?.date ? new Date(e.date) : null;
      if (!d || Number.isNaN(d.getTime())) return true;
      if (range.start && d < new Date(range.start)) return false;
      if (range.end && d > new Date(range.end)) return false;
      return true;
    });
  }, [liveExpenses, timeFilter, dateRange]);

  const productData = useMemo(() => {
    const map = {};
    filteredOrders.forEach(order => {
      (order.products || []).forEach(op => {
        const key = op.name;
        if (!key) return;
        const qty = op.quantity || 1, sp = op.price ?? 0, cp = getCostPrice(key, sp), sl = qty * sp, cl = qty * cp;
        if (!map[key]) {
          map[key] = { name: key, unitsSold: 0, sellingRevenue: 0, costRevenue: 0, revenue: 0, cost: 0, profit: 0, orders: [] };
        }
        map[key].unitsSold += qty;
        map[key].sellingRevenue += sl;
        map[key].costRevenue += cl;
        map[key].revenue += sl;
        map[key].cost += cl;
        map[key].profit += sl - cl;
        map[key].orders.push({
          orderId: order.id,
          invoiceNumber: order.id,
          date: order.date,
          time: order.time,
          customer: order.customer,
          quantity: qty,
          sellingUnit: sp,
          costUnit: cp,
          unitPrice: sp,
          total: sl,
          costTotal: cl
        });
      });
    });
    return Object.values(map).map(p => ({
      ...p,
      margin: p.sellingRevenue ? ((p.sellingRevenue - p.costRevenue) / p.sellingRevenue) * 100 : 0
    }));
  }, [filteredOrders, products]);

  const displayed = useMemo(() => {
    let l = productData.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));
    l.sort((a, b) => sortDir === 'desc' ? (b[sortField] ?? 0) - (a[sortField] ?? 0) : (a[sortField] ?? 0) - (b[sortField] ?? 0));
    return l;
  }, [productData, search, sortField, sortDir]);

  const toggleSort = f => {
    if (sortField === f) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else {
      setSortField(f);
      setSortDir('desc');
    }
  };

  const totalSell = productData.reduce((s, p) => s + p.sellingRevenue, 0);
  const totalCost = productData.reduce((s, p) => s + p.costRevenue, 0);
  const totalProfit = totalSell - totalCost;
  const totalUnits = productData.reduce((s, p) => s + p.unitsSold, 0);
  const avgMargin = productData.length ? productData.reduce((s, p) => s + p.margin, 0) / productData.length : 0;
  const topProduct = productData.reduce((t, p) => (!t || p.sellingRevenue > t.sellingRevenue) ? p : t, null);
  const chartTop = displayed.slice(0, 7);
  const chartMaxSell = Math.max(...chartTop.map(p => p.sellingRevenue), 1);

  // Selected Product insights
  const productNames = useMemo(() => productData.map(p => p.name).sort(), [productData]);
  const selectedProduct = useMemo(() => {
    if (!selectedProductName) return null;
    return productData.find(p => p.name === selectedProductName) || null;
  }, [productData, selectedProductName]);

  // City analysis parser
  const extractCity = (address) => {
    if (!address) return 'Unknown';
    const withoutPin = address.replace(/[-–]?\s*\d{6,}/g, '').trim();
    const parts = withoutPin.split(',').map(s => s.trim()).filter(Boolean);
    if (parts.length === 1) return parts[0];
    const city = parts[parts.length - 2];
    return city && city.length > 1 ? city : parts[parts.length - 1] || 'Unknown';
  };

  const cityData = useMemo(() => {
    const map = {};
    const ordersForCity = selectedProductName
      ? filteredOrders.filter(o => (o.products || []).some(p => p.name === selectedProductName))
      : filteredOrders;
    ordersForCity.forEach(order => {
      const city = extractCity(order.address);
      if (!map[city]) map[city] = { city, orders: 0, revenue: 0, units: 0, customers: new Set() };
      map[city].orders++;
      map[city].revenue += order.total || 0;
      map[city].customers.add(order.customer);
      (order.products || []).forEach(p => {
        if (!selectedProductName || p.name === selectedProductName) {
          map[city].units += p.quantity || 1;
        }
      });
    });
    return Object.values(map)
      .map(c => ({ ...c, customers: c.customers.size }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [filteredOrders, selectedProductName]);

  const exportPDF = () => {
    const rows = displayed.map((p, i) => `<tr><td>${i + 1}</td><td>${p.name}</td><td>${p.unitsSold}</td><td>₹${fmt(p.sellingRevenue)}</td><td>₹${fmt(p.costRevenue)}</td><td>₹${fmt(p.profit)}</td><td>${p.margin.toFixed(1)}%</td><td>${p.orders.length}</td></tr>`).join('');
    const html = `<html><head><title>Analytics</title><style>body{font-family:Arial;margin:20px}h1{color:#16a34a}table{width:100%;border-collapse:collapse}th{background:#f0fdf4;color:#15803d;padding:8px;font-size:12px;text-align:left}td{padding:8px;border-bottom:1px solid #e5e7eb;font-size:12px}</style></head><body><h1>🌿 Manthuli – Analytics Report</h1><p>${new Date().toLocaleString()}</p><table><thead><tr><th>#</th><th>Product</th><th>Units</th><th>Selling Rev</th><th>Cost Rev</th><th>Profit</th><th>Margin</th><th>Invoices</th></tr></thead><tbody>${rows}</tbody></table></body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    w.print();
  };

  const bulkPrintInvoices = () => {
    if (!filteredOrders.length) return alert("No invoices found for the selected date range.");
    
    const pagesHtml = filteredOrders.map((order) => {
      const invNum = `INV-${new Date().getFullYear()}-${String(order.id || '').replace(/\D/g, '').padStart(4, '0') || Math.floor(Math.random() * 9000 + 1000)}`;
      const items = order.products || [];
      const subtotal = order.total || 0;
      const gstAmt = subtotal * 0;
      const grandTotal = subtotal + gstAmt;
      const custName = order.customer || 'Guest';

      return `
<div class="page">
  <div class="header">
    <div class="header-top">
      <div class="brand">
        <div class="brand-icon">🌿</div>
        <div>
          <div class="brand-name">Manthuli Organic Shop</div>
          <div class="brand-sub">Fresh • Organic • Natural</div>
        </div>
      </div>
      <div class="inv-badge"><div class="label">Invoice</div><div class="num">${invNum}</div></div>
    </div>
    <div class="header-meta">
      <div class="meta-item"><div class="label">Invoice Date</div><div class="val">${order.date || new Date().toLocaleDateString('en-GB').replaceAll('/', '.')}</div></div>
      <div class="meta-item"><div class="label">Order Ref</div><div class="val">${order.id || '—'}</div></div>
      <div class="meta-item"><div class="label">Payment</div><div class="val">COD</div></div>
    </div>
  </div>
  <div class="body">
    <div class="two-col">
      <div class="info-box"><h4>Bill To</h4><p class="main">${custName}</p><p style="margin-top:6px;color:#64748b;font-size:12px">${order.address || ''}</p></div>
      <div class="info-box"><h4>From</h4><p class="main">Manthuli Organic Shop</p><p>📞 +91-9876543210</p><p>📧 info@manthuliorganic.com</p><p style="margin-top:6px;color:#64748b;font-size:12px">123 Organic Lane, Green City</p></div>
    </div>
    <table>
      <thead><tr><th>#</th><th>Item Description</th><th>Weight / Unit</th><th>Qty</th><th>Unit Price</th><th>Amount</th></tr></thead>
      <tbody>
        ${items.map((it, ii) => `<tr><td style="color:#94a3b8;font-weight:600">${String(ii + 1).padStart(2, '0')}</td><td><div class="item-name">${it.name}</div><div class="item-sub">Organic · Premium Quality</div></td><td>${it.weight || '—'} ${it.unit || ''}</td><td style="text-align:center">${it.quantity || 1}</td><td>₹${(it.price || 0).toLocaleString('en-IN')}</td><td style="font-weight:700;color:#111827">₹${((it.price || 0) * (it.quantity || 1)).toLocaleString('en-IN')}</td></tr>`).join('')}
      </tbody>
    </table>
    <div class="totals">
      <div class="total-row"><span>Subtotal</span><span>₹${subtotal.toLocaleString('en-IN')}</span></div>
      <div class="total-row"><span>GST (0%)</span><span>₹${gstAmt.toLocaleString('en-IN')}</span></div>
      <div class="total-row"><span>Delivery</span><span style="color:#16a34a;font-weight:700">FREE</span></div>
      <div class="total-row grand"><span>Grand Total</span><span class="amount">₹${grandTotal.toLocaleString('en-IN')}</span></div>
    </div>
  </div>
  <div class="footer">
    <div class="footer-left"><strong>Payment:</strong> Cash on Delivery<br/>GSTIN: 29AAACH7409R1ZZ</div>
    <div class="thanks">Thank you! 🌿<span>We appreciate your business</span></div>
  </div>
  <div class="watermark">Computer-generated invoice · No signature required</div>
</div>`;
    }).join('<div class="page-break"></div>');

    const w = window.open('', '_blank', 'width=820,height=950');
    w.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Bulk Invoices</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:'Inter',sans-serif;background:#f1f5f9;padding:30px;color:#1e293b}
    .page{background:#fff;max-width:720px;margin:0 auto;border-radius:16px;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,0.12)}
    .header{background:linear-gradient(135deg,#14532d 0%,#16a34a 60%,#22c55e 100%);padding:36px 40px;color:#fff}
    .header-top{display:flex;justify-content:space-between;align-items:flex-start}
    .brand{display:flex;align-items:center;gap:12px}
    .brand-icon{width:52px;height:52px;background:rgba(255,255,255,0.2);border-radius:14px;display:flex;align-items:center;justify-content:center;font-size:26px}
    .brand-name{font-size:24px;font-weight:900;letter-spacing:-0.5px}
    .brand-sub{font-size:12px;opacity:0.8;margin-top:3px;letter-spacing:1px}
    .inv-badge{background:rgba(255,255,255,0.2);border-radius:10px;padding:10px 18px;text-align:right}
    .inv-badge .label{font-size:10px;opacity:0.7;text-transform:uppercase;letter-spacing:1.5px}
    .inv-badge .num{font-size:20px;font-weight:800;margin-top:2px}
    .header-meta{display:flex;gap:32px;margin-top:24px;padding-top:20px;border-top:1px solid rgba(255,255,255,0.2)}
    .meta-item .label{font-size:10px;opacity:0.7;text-transform:uppercase;letter-spacing:1px}
    .meta-item .val{font-size:14px;font-weight:700;margin-top:3px}
    .body{padding:36px 40px}
    .two-col{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-bottom:32px}
    .info-box{background:#f8fafc;border-radius:12px;padding:18px}
    .info-box h4{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#94a3b8;margin-bottom:10px}
    .info-box p{font-size:13px;color:#374151;line-height:1.7}
    .info-box .main{font-size:15px;font-weight:700;color:#111827}
    table{width:100%;border-collapse:collapse;margin-bottom:28px}
    thead tr{background:linear-gradient(135deg,#f0fdf4,#dcfce7)}
    th{padding:12px 14px;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#15803d}
    th:last-child,td:last-child{text-align:right}
    td{padding:13px 14px;font-size:13px;color:#374151;border-bottom:1px solid #f1f5f9}
    tr:last-child td{border-bottom:none}
    tbody tr:hover{background:#fafffe}
    .item-name{font-weight:600;color:#111827}
    .item-sub{font-size:11px;color:#94a3b8;margin-top:3px}
    .totals{margin-left:auto;width:260px}
    .total-row{display:flex;justify-content:space-between;padding:7px 0;font-size:13px;color:#64748b}
    .total-row.grand{border-top:2px solid #16a34a;margin-top:6px;padding-top:12px;font-size:17px;font-weight:800;color:#111827}
    .total-row.grand .amount{color:#16a34a}
    .footer{background:#f8fafc;border-top:1px solid #e2e8f0;padding:24px 40px;display:flex;justify-content:space-between;align-items:center}
    .footer-left{font-size:12px;color:#64748b;line-height:1.8}
    .thanks{font-size:13px;font-weight:700;color:#16a34a}
    .watermark{text-align:center;padding:16px;font-size:11px;color:#cbd5e1}
    @media print{
      body{background:#fff;padding:0}
      .page{box-shadow:none;border-radius:0;margin:0 auto}
      .page-break{page-break-after:always;line-height:0}
      .no-print{display:none !important}
    }
  </style>
</head>
<body>
  <div class="no-print" style="position:sticky;top:0;background:linear-gradient(135deg,#16a34a,#15803d);padding:16px 30px;color:#fff;display:flex;justify-content:space-between;align-items:center;z-index:100;box-shadow:0 4px 16px rgba(0,0,0,0.15)">
    <div style="font-weight:700;font-size:16px">Generated Invoices (${filteredOrders.length})</div>
    <button onclick="window.print()" style="background:#fff;border:none;color:#16a34a;padding:8px 20px;border-radius:8px;font-weight:700;cursor:pointer;font-size:14px;box-shadow:0 2px 8px rgba(0,0,0,0.1)">🖨️ Print All</button>
  </div>
  <div style="padding:30px">
    ${pagesHtml}
  </div>
</body>
</html>`);
    w.document.close();
  };

  const SortIcon = ({ f }) => sortField !== f 
    ? <ChevronDown size={12} className="inline ml-1 text-slate-300" />
    : (sortDir === 'desc'
      ? <ChevronDown size={12} className="inline ml-1 text-emerald-600" />
      : <ChevronUp size={12} className="inline ml-1 text-emerald-600" />);

  // Determine scoped values based on selected product or global range
  const dispSell = selectedProduct ? selectedProduct.sellingRevenue : totalSell;
  const dispCost = selectedProduct ? selectedProduct.costRevenue : totalCost;
  const dispProfit = selectedProduct ? selectedProduct.profit : totalProfit;
  const dispOrders = selectedProduct ? selectedProduct.orders.length : filteredOrders.length;
  const dispUnits = selectedProduct ? selectedProduct.unitsSold : totalUnits;

  const totalExpensesAmt = (filteredExpenses || []).reduce((s, e) => s + (Number(e.totalAmount) || 0), 0);
  const netAfterExpenses = dispProfit - totalExpensesAmt;

  const metrics = [
    { icon: DollarSign, label: selectedProduct ? `${selectedProduct.name} – Sell` : 'Selling Revenue', value: `₹${fmt(dispSell)}`, sub: '+12.5%', iconBg: 'from-emerald-500 to-emerald-600 shadow-emerald-500/20', badgeBg: 'bg-emerald-50 text-emerald-700 border-emerald-100', border: 'border-emerald-100/70', bg: 'bg-emerald-50/10' },
    { icon: Package, label: selectedProduct ? `${selectedProduct.name} – Cost` : 'Cost Revenue', value: `₹${fmt(dispCost)}`, sub: '+8.1%', iconBg: 'from-rose-500 to-rose-600 shadow-rose-500/20', badgeBg: 'bg-rose-50 text-rose-700 border-rose-100', border: 'border-rose-100/70', bg: 'bg-rose-50/10' },
    { icon: TrendingUp, label: selectedProduct ? `${selectedProduct.name} – Profit` : 'Gross Profit', value: `₹${fmt(dispProfit)}`, sub: '+18.3%', iconBg: 'from-purple-500 to-purple-600 shadow-purple-500/20', badgeBg: 'bg-purple-50 text-purple-700 border-purple-100', border: 'border-purple-100/70', bg: 'bg-purple-50/10' },
    { icon: ShoppingCart, label: selectedProduct ? `${selectedProduct.name} – Orders` : 'Total Orders', value: dispOrders, sub: '+5.2%', iconBg: 'from-blue-500 to-blue-600 shadow-blue-500/20', badgeBg: 'bg-blue-50 text-blue-700 border-blue-100', border: 'border-blue-100/70', bg: 'bg-blue-50/10' },
    { icon: Tag, label: selectedProduct ? `${selectedProduct.name} – Units` : 'Units Sold', value: dispUnits, sub: '+15.3%', iconBg: 'from-amber-500 to-amber-600 shadow-amber-500/20', badgeBg: 'bg-amber-50 text-amber-700 border-amber-100', border: 'border-amber-100/70', bg: 'bg-amber-50/10' },
    { icon: CreditCard, label: 'Total Expenses', value: `₹${fmt(totalExpensesAmt)}`, sub: 'Live Check', iconBg: 'from-teal-500 to-teal-600 shadow-teal-500/20', badgeBg: 'bg-teal-50 text-teal-700 border-teal-100', border: 'border-teal-100/70', bg: 'bg-teal-50/10' },
    { icon: Layers, label: 'Net (After Expenses)', value: `₹${fmt(netAfterExpenses)}`, sub: 'Calculated', iconBg: 'from-slate-700 to-slate-800 shadow-slate-700/20', badgeBg: 'bg-slate-100 text-slate-700 border-slate-200', border: 'border-slate-200/60', bg: 'bg-slate-50/40' },
  ];

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f8faf9] to-[#f4f8f6]">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-slate-200 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-500 font-semibold text-sm">Aggregating report database…</p>
      </div>
    </div>
  );

  return (
    <div className="ui-page animate-fade-in">
      {/* HEADER CONTROLS */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight">Financial Analytics</h1>
          <p className="text-slate-500 mt-1 font-medium text-sm">Review revenues, casting margins, location details, and expenses.</p>
        </div>
        
        {/* ACTION BUTTONS */}
        <div className="flex gap-2">
          <button onClick={bulkPrintInvoices} className="ui-btn-secondary flex items-center gap-2">
            <Printer size={15} className="text-emerald-600" />
            View Invoices
          </button>
          <button onClick={exportPDF} className="ui-btn-primary flex items-center gap-2">
            <Download size={15} />
            Export Report
          </button>
        </div>
      </div>

      {/* FILTER PANEL */}
      <div className="ui-card ui-card-pad mb-6">
        <div className="flex flex-wrap items-center gap-3">
          {[
            { k: 'all', l: 'All Time' },
            { k: 'today', l: 'Today' },
            { k: 'week', l: 'This Week' },
            { k: 'month', l: 'This Month' },
            { k: 'year', l: 'This Year' },
            { k: 'custom', l: 'Custom Range' },
          ].map(tab => (
            <button
              key={tab.k}
              onClick={() => setTimeFilter(tab.k)}
              className={`px-4 py-2 rounded-xl text-xs font-bold tracking-wide transition-all duration-200 cursor-pointer border ${
                timeFilter === tab.k
                  ? 'bg-gradient-to-r from-emerald-600 to-green-700 text-white border-transparent shadow-[0_4px_12px_rgba(16,185,129,0.2)]'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {tab.l}
            </button>
          ))}

          {/* Date range picker for custom filter */}
          {timeFilter === 'custom' && (
            <div className="flex items-center gap-2 animate-fade-in">
              <input
                type="date"
                value={dateRange.start}
                onChange={e => setDateRange(r => ({ ...r, start: e.target.value }))}
                className="ui-input !py-1.5 !px-3 text-xs bg-white"
              />
              <span className="text-slate-400 font-bold text-xs">to</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={e => setDateRange(r => ({ ...r, end: e.target.value }))}
                className="ui-input !py-1.5 !px-3 text-xs bg-white"
              />
              {(dateRange.start || dateRange.end) && (
                <button
                  onClick={() => setDateRange({ start: '', end: '' })}
                  className="px-2.5 py-1.5 text-xs font-bold text-slate-500 hover:text-slate-800"
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>

        {/* Selected parameters label */}
        <div className="mt-4 pt-3 border-t border-slate-100 text-slate-400 text-xs font-semibold flex items-center gap-2 flex-wrap">
          <span>Active filter:</span>
          <span className="text-emerald-600 font-bold bg-emerald-50 border border-emerald-100/60 px-2 py-0.5 rounded-md">
            {timeFilter === 'today' ? 'Today' : timeFilter === 'week' ? 'This Week' : timeFilter === 'month' ? 'This Month' : timeFilter === 'year' ? 'This Year' : timeFilter === 'custom' ? 'Custom Date Range' : 'All Time'}
          </span>
          <span>·</span>
          <span>Records parsed: <strong className="text-slate-700">{filteredOrders.length} orders</strong></span>
          {selectedProductName && (
            <>
              <span>·</span>
              <span className="text-purple-600 font-bold bg-purple-50 border border-purple-100/60 px-2 py-0.5 rounded-md">
                Isolated: {selectedProductName}
              </span>
            </>
          )}
        </div>
      </div>

      {/* METRIC CARDS GRID */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
        {metrics.map((m, i) => {
          const Icon = m.icon;
          return (
            <div key={i} className={`ui-card ${m.border} ${m.bg} flex flex-col justify-between p-4 cursor-default transition-all duration-300 hover:-translate-y-1 hover:shadow-md`}>
              <div className="flex items-center justify-between gap-1.5 mb-3">
                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${m.iconBg} flex items-center justify-center text-white shadow-md`}>
                  <Icon size={15} />
                </div>
                <span className={`inline-flex items-center text-[9px] font-extrabold px-1.5 py-0.5 rounded-md border ${m.badgeBg}`}>
                  {m.sub}
                </span>
              </div>
              <div>
                <p className="text-slate-400 text-[9px] font-bold uppercase tracking-wider mb-1 line-clamp-1">{m.label}</p>
                <p className="text-slate-800 font-black text-sm md:text-base tracking-tight">{m.value}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* EXPENSE RECORDS */}
      <div className="ui-card ui-card-pad mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-600 to-teal-700 flex items-center justify-center shadow-lg shadow-teal-600/20 text-white">
              <CreditCard size={16} />
            </div>
            <div>
              <h3 className="text-base font-extrabold text-slate-800">Operating Expenses</h3>
              <p className="text-slate-400 text-[11px] font-medium">Expenses registered during the active filter window</p>
            </div>
          </div>
          <span className="self-start sm:self-center font-bold text-xs text-teal-700 bg-teal-50 border border-teal-100 px-3.5 py-1.5 rounded-xl">
            Period Total: ₹{fmt(totalExpensesAmt)}
          </span>
        </div>

        <div className="ui-table-wrap border border-slate-100 max-h-[300px] overflow-y-auto ui-scrollbar">
          <table className="ui-table">
            <thead className="sticky top-0 bg-white z-10 shadow-[0_1px_0_0_#f1f5f9]">
              <tr>
                <th className="ui-th !py-2.5">Date</th>
                <th className="ui-th !py-2.5">Category</th>
                <th className="ui-th !py-2.5">Description</th>
                <th className="ui-th !py-2.5 text-right">Paid</th>
                <th className="ui-th !py-2.5 text-right">Pending</th>
                <th className="ui-th !py-2.5 text-right">Total</th>
                <th className="ui-th !py-2.5 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {(filteredExpenses || []).slice().sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)).map((e, idx) => (
                <tr key={e.id ?? idx} className="hover:bg-slate-50/45 transition-colors">
                  <td className="ui-td !py-2.5 text-slate-500 font-medium whitespace-nowrap">{e.date || '—'}</td>
                  <td className="ui-td !py-2.5 text-slate-800 font-bold">{String(e.category || '—').replace(/_/g, ' ')}</td>
                  <td className="ui-td !py-2.5 text-slate-500 text-xs truncate max-w-[280px]">{e.description || '—'}</td>
                  <td className="ui-td !py-2.5 text-right text-emerald-600 font-extrabold">₹{fmt(Number(e.paidAmount) || 0)}</td>
                  <td className="ui-td !py-2.5 text-right text-rose-600 font-bold">₹{fmt(Number(e.pendingAmount) || 0)}</td>
                  <td className="ui-td !py-2.5 text-right text-slate-800 font-black">₹{fmt(Number(e.totalAmount) || 0)}</td>
                  <td className="ui-td !py-2.5 text-center">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                      e.status === 'paid' 
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                        : 'bg-amber-50 text-amber-700 border-amber-100'
                    }`}>
                      {e.status || 'pending'}
                    </span>
                  </td>
                </tr>
              ))}
              {(!filteredExpenses || filteredExpenses.length === 0) && (
                <tr>
                  <td colSpan={7} className="ui-td text-center text-slate-400 py-8">
                    No operating expenses recorded in this timeframe.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PRODUCT ANALYTICS SEARCH / ISOLATOR */}
      <div className="ui-card ui-card-pad mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-600 to-purple-700 flex items-center justify-center shadow-lg shadow-purple-600/20 text-white">
              <Search size={16} />
            </div>
            <div>
              <h3 className="text-base font-extrabold text-slate-800">Isolate Product Metrics</h3>
              <p className="text-slate-400 text-[11px] font-medium">Pinpoint revenue streams, totals and unit counts for a single product</p>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Type to filter list..."
                value={productSearch}
                onChange={e => setProductSearch(e.target.value)}
                className="ui-input !pl-9 !py-2 text-xs w-[200px]"
              />
            </div>
            <select
              value={selectedProductName}
              onChange={e => setSelectedProductName(e.target.value)}
              className="ui-input !py-2 text-xs font-semibold bg-white cursor-pointer min-w-[200px]"
            >
              <option value="">— Show All Products —</option>
              {productNames.filter(n => n.toLowerCase().includes(productSearch.toLowerCase())).map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            {selectedProductName && (
              <button
                onClick={() => { setSelectedProductName(''); setProductSearch(''); }}
                className="px-3.5 py-2 rounded-xl text-xs font-bold bg-rose-50 text-rose-700 hover:bg-rose-100 transition-colors border border-rose-100"
              >
                ✕ Reset
              </button>
            )}
          </div>
        </div>

        {/* Selected Product recent sales drawer */}
        {selectedProduct && (
          <div className="mt-4 pt-4 border-t border-slate-100 animate-fade-in">
            <p className="text-purple-700 text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-purple-600 animate-ping" />
              Displaying Sales Timeline for: {selectedProduct.name}
            </p>
            
            <div className="bg-slate-50/50 rounded-2xl border border-slate-100 overflow-hidden">
              <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
                <span className="text-xs font-bold text-slate-700">Recent Transactions</span>
                <span className="text-[10px] font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-md">
                  {selectedProduct.orders.length} matches
                </span>
              </div>
              <div className="max-h-[180px] overflow-y-auto ui-scrollbar">
                <table className="ui-table">
                  <thead>
                    <tr>
                      <th className="ui-th !py-2 !px-4 text-[10px]">Order ID</th>
                      <th className="ui-th !py-2 !px-4 text-[10px]">Date</th>
                      <th className="ui-th !py-2 !px-4 text-[10px]">Customer</th>
                      <th className="ui-th !py-2 !px-4 text-center text-[10px]">Quantity</th>
                      <th className="ui-th !py-2 !px-4 text-right text-[10px]">Unit Selling Price</th>
                      <th className="ui-th !py-2 !px-4 text-right text-[10px]">Total Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50 text-xs bg-white">
                    {selectedProduct.orders.slice().reverse().slice(0, 8).map((o, i) => (
                      <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                        <td className="ui-td !py-2 !px-4 text-purple-600 font-extrabold">{o.orderId}</td>
                        <td className="ui-td !py-2 !px-4 text-slate-500 font-medium">{o.date}</td>
                        <td className="ui-td !py-2 !px-4 text-slate-800 font-bold">{o.customer}</td>
                        <td className="ui-td !py-2 !px-4 text-center text-slate-700 font-black">{o.quantity}</td>
                        <td className="ui-td !py-2 !px-4 text-right text-emerald-600 font-extrabold">₹{o.sellingUnit}</td>
                        <td className="ui-td !py-2 !px-4 text-right text-slate-900 font-black">₹{fmt(o.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* SALES BY LOCATION */}
      <div className="ui-card ui-card-pad mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-600 to-cyan-700 flex items-center justify-center shadow-lg shadow-cyan-600/20 text-white">
              <Activity size={16} />
            </div>
            <div>
              <h3 className="text-base font-extrabold text-slate-800">Sales by Location</h3>
              <p className="text-slate-400 text-[11px] font-medium">
                {selectedProductName ? `Regions where "${selectedProductName}" is performing` : 'Distribution of revenue across delivery centers'}
              </p>
            </div>
          </div>
          
          <div className="relative">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search city/town..."
              value={citySearch}
              onChange={e => setCitySearch(e.target.value)}
              className="ui-input !pl-9 !py-2 text-xs w-[200px]"
            />
          </div>
        </div>

        {cityData.length === 0 ? (
          <div className="text-center py-10 text-slate-400 font-medium text-sm">
            No regional analytics compiled yet. Delivery addresses are needed on orders.
          </div>
        ) : (
          <>
            {/* Top 4 City Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5 mb-5">
              {cityData.slice(0, 4).map((c, i) => {
                const schemes = [
                  { text: 'text-cyan-700', bg: 'bg-cyan-50/50', border: 'border-cyan-150', val: 'text-cyan-600' },
                  { text: 'text-emerald-700', bg: 'bg-emerald-50/50', border: 'border-emerald-150', val: 'text-emerald-600' },
                  { text: 'text-purple-700', bg: 'bg-purple-50/50', border: 'border-purple-150', val: 'text-purple-600' },
                  { text: 'text-amber-700', bg: 'bg-amber-50/50', border: 'border-amber-150', val: 'text-amber-600' }
                ];
                const sc = schemes[i] || schemes[0];
                return (
                  <div key={c.city} className={`rounded-2xl border ${sc.border} ${sc.bg} p-4 text-center flex flex-col justify-between`}>
                    <div>
                      <p className="text-slate-400 text-[8px] font-bold uppercase tracking-wider mb-1">Rank #{i + 1} Center</p>
                      <h4 className={`font-black text-sm truncate ${sc.text}`}>📍 {c.city}</h4>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5 mt-4 pt-3 border-t border-slate-200/50">
                      <div>
                        <span className="text-[8px] font-bold text-slate-400 block mb-0.5 uppercase">Revenue</span>
                        <span className={`text-xs font-black ${sc.val}`}>₹{fmt(c.revenue)}</span>
                      </div>
                      <div>
                        <span className="text-[8px] font-bold text-slate-400 block mb-0.5 uppercase">Orders</span>
                        <span className={`text-xs font-black ${sc.val}`}>{c.orders}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* City Table */}
            <div className="ui-table-wrap border border-slate-100 max-h-[320px] overflow-y-auto ui-scrollbar">
              <table className="ui-table">
                <thead>
                  <tr>
                    <th className="ui-th !py-2.5">#</th>
                    <th className="ui-th !py-2.5">City / Delivery Zone</th>
                    <th className="ui-th !py-2.5 text-center">Orders</th>
                    <th className="ui-th !py-2.5 text-right">Gross Revenue</th>
                    <th className="ui-th !py-2.5 text-center">Items Sold</th>
                    <th className="ui-th !py-2.5 text-center">Unique Customers</th>
                    <th className="ui-th !py-2.5">Share of Sales</th>
                    <th className="ui-th !py-2.5 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {cityData.filter(c => c.city.toLowerCase().includes(citySearch.toLowerCase())).map((c, i) => {
                    const totalRev = cityData.reduce((s, x) => s + x.revenue, 0);
                    const pct = totalRev ? (c.revenue / totalRev) * 100 : 0;
                    return (
                      <tr
                        key={c.city}
                        onClick={() => setSelectedCity(c.city)}
                        className="hover:bg-slate-50/50 transition-all duration-150 cursor-pointer"
                      >
                        <td className="ui-td !py-2.5 text-slate-400 font-semibold">{i + 1}</td>
                        <td className="ui-td !py-2.5 font-bold text-slate-800">
                          <span className="inline-flex items-center gap-1">📍 {c.city}</span>
                        </td>
                        <td className="ui-td !py-2.5 text-center text-blue-600 font-black">{c.orders}</td>
                        <td className="ui-td !py-2.5 text-right text-emerald-600 font-black">₹{fmt(c.revenue)}</td>
                        <td className="ui-td !py-2.5 text-center text-slate-700 font-bold">{c.units}</td>
                        <td className="ui-td !py-2.5 text-center text-slate-600 font-medium">{c.customers}</td>
                        <td className="ui-td !py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs font-bold text-slate-600">{pct.toFixed(1)}%</span>
                          </div>
                        </td>
                        <td className="ui-td !py-2.5 text-center" onClick={e => e.stopPropagation()}>
                          <button
                            onClick={() => setSelectedCity(c.city)}
                            className="px-3.5 py-1.5 rounded-lg text-xs font-bold bg-cyan-50 text-cyan-700 border border-cyan-100 hover:bg-cyan-100 transition-colors"
                          >
                            Drill Down
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* DAILY REVENUE FLOW & PROFIABILITY */}
      <div className="ui-card ui-card-pad mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-600 to-indigo-700 flex items-center justify-center shadow-lg shadow-indigo-600/20 text-white">
            <Activity size={16} />
          </div>
          <div>
            <h3 className="text-base font-extrabold text-slate-800">Daily Financial Breakdown</h3>
            <p className="text-slate-400 text-[11px] font-medium">Timeline of sales revenues, cost margins and daily profits</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[360px] overflow-y-auto ui-scrollbar pr-1">
          {Object.entries(
            filteredOrders.reduce((g, o) => {
              const d = o.date;
              if (!g[d]) g[d] = { ids: [], sell: 0, cost: 0 };
              g[d].ids.push(o.id);
              g[d].sell += (o.products || []).reduce((s, p) => s + p.price * (p.quantity || 1), 0);
              g[d].cost += (o.products || []).reduce((s, p) => s + getCostPrice(p.name, p.price) * (p.quantity || 1), 0);
              return g;
            }, {})
          )
            .sort(([a], [b]) => {
              const parseDate = s => {
                const [d, m, y] = s.split('.');
                return new Date(`${y}-${m}-${d}`);
              };
              return parseDate(b) - parseDate(a);
            })
            .map(([date, d]) => {
              const profit = d.sell - d.cost;
              const margin = d.sell ? (profit / d.sell) * 100 : 0;
              const costPct = d.sell ? (d.cost / d.sell) * 100 : 0;
              return (
                <div key={date} className="bg-slate-50/60 border border-slate-100/80 rounded-2xl p-4 transition-colors hover:bg-slate-50">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="text-slate-800 font-bold text-sm">{date}</p>
                      <span className="text-[10px] font-bold text-slate-400 bg-slate-100 border border-slate-200/50 px-1.5 py-0.5 rounded-md mt-1 inline-block">
                        {d.ids.length} order{d.ids.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-slate-400 text-[8px] font-bold uppercase tracking-wider mb-0.5">Daily Profit</p>
                      <p className="text-purple-700 font-black text-sm">₹{fmt(profit)}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 py-2.5 border-t border-b border-slate-100 mb-3 text-center">
                    <div>
                      <span className="text-[8px] font-bold text-slate-400 block uppercase mb-0.5">Sell</span>
                      <span className="text-xs font-extrabold text-emerald-600">₹{fmt(d.sell)}</span>
                    </div>
                    <div>
                      <span className="text-[8px] font-bold text-slate-400 block uppercase mb-0.5">Cost</span>
                      <span className="text-xs font-bold text-rose-600">₹{fmt(d.cost)}</span>
                    </div>
                    <div>
                      <span className="text-[8px] font-bold text-slate-400 block uppercase mb-0.5">Margin</span>
                      <span className={`text-xs font-black ${margin >= 30 ? 'text-emerald-700' : margin >= 15 ? 'text-amber-700' : 'text-rose-700'}`}>
                        {margin.toFixed(1)}%
                      </span>
                    </div>
                  </div>

                  <div className="relative h-2 bg-slate-200 rounded-full overflow-hidden shadow-inner">
                    <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-500 to-green-600 rounded-full" style={{ width: '100%' }} />
                    <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-rose-500 to-red-500 rounded-full opacity-90" style={{ width: `${costPct}%` }} />
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* DETAILED PRODUCT REPORT */}
      <div className="ui-card mb-6 overflow-hidden border border-slate-100">
        <div className="px-6 py-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-gradient-to-r from-purple-50/20 to-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-700 flex items-center justify-center shadow-lg shadow-purple-600/20 text-white">
              <BarChart3 size={16} />
            </div>
            <div>
              <h3 className="text-base font-extrabold text-slate-800">Product Profitability Index</h3>
              <p className="text-slate-400 text-[11px] font-medium">Detailed itemized costs, casting margins, revenues, and transaction count</p>
            </div>
          </div>
          
          <div className="relative">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search product catalog..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="ui-input !pl-9 !py-2 text-xs w-[200px]"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="ui-table">
            <thead>
              <tr className="bg-slate-50/30">
                <th className="ui-th !py-3 w-10 text-left">#</th>
                <th className="ui-th !py-3 w-56 text-left cursor-pointer select-none" onClick={() => toggleSort('name')}>
                  Product <SortIcon f="name" />
                </th>
                <th className="ui-th !py-3 text-center cursor-pointer select-none" onClick={() => toggleSort('unitsSold')}>
                  Units Sold <SortIcon f="unitsSold" />
                </th>
                <th className="ui-th !py-3 text-right cursor-pointer select-none" onClick={() => toggleSort('sellingRevenue')}>
                  Selling Rev <SortIcon f="sellingRevenue" />
                </th>
                <th className="ui-th !py-3 text-right cursor-pointer select-none" onClick={() => toggleSort('costRevenue')}>
                  Costing Bills <SortIcon f="costRevenue" />
                </th>
                <th className="ui-th !py-3 text-right cursor-pointer select-none" onClick={() => toggleSort('profit')}>
                  Gross Profit <SortIcon f="profit" />
                </th>
                <th className="ui-th !py-3 text-center cursor-pointer select-none" onClick={() => toggleSort('margin')}>
                  Profit Margin <SortIcon f="margin" />
                </th>
                <th className="ui-th !py-3 text-center">Invoices</th>
                <th className="ui-th !py-3 text-center">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {displayed.length === 0 && (
                <tr>
                  <td colSpan={9} className="ui-td text-center text-slate-400 py-12">
                    {search ? 'No products matching your search parameter.' : 'No sales logs parsed in database.'}
                  </td>
                </tr>
              )}
              {displayed.map((p, i) => {
                const costPercent = p.sellingRevenue ? (p.costRevenue / p.sellingRevenue) * 100 : 0;
                return (
                  <tr
                    key={p.name}
                    onClick={() => setSelProduct(p)}
                    className="hover:bg-purple-50/10 cursor-pointer transition-colors"
                  >
                    <td className="ui-td font-semibold text-slate-400">{i + 1}</td>
                    <td className="ui-td">
                      <p className="text-slate-800 font-bold mb-1 text-sm">{p.name}</p>
                      <div className="w-28 h-1 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500" style={{ width: '100%' }} />
                        <div className="h-full bg-rose-500" style={{ width: `${costPercent}%`, marginTop: '-4px' }} />
                      </div>
                    </td>
                    <td className="ui-td text-center text-slate-700 font-black">{p.unitsSold}</td>
                    <td className="ui-td text-right text-emerald-600 font-black">₹{fmt(p.sellingRevenue)}</td>
                    <td className="ui-td text-right text-rose-600 font-bold">
                      ₹{fmt(p.costRevenue)}
                      <span className="block text-[10px] text-slate-400 mt-0.5 font-medium">{costPercent.toFixed(0)}% of sales</span>
                    </td>
                    <td className="ui-td text-right text-purple-700 font-black">₹{fmt(p.profit)}</td>
                    <td className="ui-td text-center">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-bold border ${
                        p.margin >= 40 
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                          : p.margin >= 25
                            ? 'bg-amber-50 text-amber-700 border-amber-100'
                            : 'bg-rose-50 text-rose-700 border-rose-100'
                      }`}>
                        {p.margin.toFixed(1)}%
                      </span>
                    </td>
                    <td className="ui-td text-center">
                      <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100">
                        {p.orders.length}
                      </span>
                    </td>
                    <td className="ui-td text-center" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => setSelProduct(p)}
                        className="ui-btn-primary !px-3 !py-1.5 !rounded-lg text-xs flex items-center justify-center gap-1 shadow-purple-200"
                      >
                        <Eye size={12} />
                        Details
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Product summary tray */}
        {displayed.length > 0 && (
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-xs font-semibold">
            <span className="text-slate-500">
              Listed: <strong className="text-slate-700">{displayed.length} products</strong> · Volume: <strong className="text-slate-700">{displayed.reduce((s, p) => s + p.unitsSold, 0)} units sold</strong>
            </span>
            <div className="flex flex-wrap gap-4">
              {[
                { label: 'Revenues', val: displayed.reduce((s, p) => s + p.sellingRevenue, 0), c: 'text-emerald-700', bullet: 'bg-emerald-500' },
                { label: 'Costing', val: displayed.reduce((s, p) => s + p.costRevenue, 0), c: 'text-rose-700', bullet: 'bg-rose-500' },
                { label: 'Net Profit', val: displayed.reduce((s, p) => s + p.profit, 0), c: 'text-purple-700', bullet: 'bg-purple-500' }
              ].map(x => (
                <span key={x.label} className={`flex items-center gap-1.5 font-bold ${x.c}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${x.bullet}`} />
                  {x.label}: ₹{fmt(x.val)}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* PRICING & STOCK OVERVIEW */}
      <div className="ui-card mb-6 overflow-hidden border border-slate-100">
        <div className="px-6 py-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-gradient-to-r from-emerald-50/10 to-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-600 to-green-700 flex items-center justify-center shadow-lg shadow-emerald-600/20 text-white">
              <Layers size={16} />
            </div>
            <div>
              <h3 className="text-base font-extrabold text-slate-800">Inventory Valuation & Margin Index</h3>
              <p className="text-slate-400 text-[11px] font-medium">Standard unit prices, costing structures, current storage levels, and stock alerts</p>
            </div>
          </div>
          
          <div className="inline-flex items-center gap-1.5 bg-cyan-50 border border-cyan-100 px-3 py-1.5 rounded-xl text-cyan-700 font-bold text-xs">
            <Warehouse size={13} />
            Catalog: {products.length} Items
          </div>
        </div>

        {/* Valuation totals breakdown */}
        {(() => {
          const totalSP = products.reduce((s, p) => s + (Number(p.price) || 0), 0);
          const totalCP = products.reduce((s, p) => s + (Number(p.castingPrice) || 0), 0);
          const totalStock = products.reduce((s, p) => s + (Number(p.stock) || 0), 0);
          const outOfStock = products.filter(p => (Number(p.stock) || 0) === 0).length;
          const lowStock = products.filter(p => (Number(p.stock) || 0) > 0 && (Number(p.stock) || 0) < 40).length;
          
          return (
            <div className="grid grid-cols-2 md:grid-cols-6 border-b border-slate-100 bg-slate-50/30">
              {[
                { label: 'Catalog Products', value: products.length, color: 'text-cyan-700', bg: 'bg-cyan-50/20' },
                { label: 'Retail Price Sum', value: `₹${fmt(totalSP)}`, color: 'text-emerald-700', bg: 'bg-emerald-50/20' },
                { label: 'Casting Price Sum', value: `₹${fmt(totalCP)}`, color: 'text-rose-700', bg: 'bg-rose-50/20' },
                { label: 'Stock Valuation', value: totalStock, color: 'text-purple-700', bg: 'bg-purple-50/20' },
                { label: 'Low Stock warnings', value: lowStock, color: 'text-amber-700', bg: 'bg-amber-50/20' },
                { label: 'Deficit (Out)', value: outOfStock, color: 'text-red-600', bg: 'bg-red-50/20' }
              ].map((s, i) => (
                <div key={i} className={`p-4 text-center border-r border-slate-100 last:border-r-0 ${s.bg}`}>
                  <p className="text-slate-400 text-[9px] font-bold uppercase tracking-wider mb-1">{s.label}</p>
                  <p className={`font-black text-sm md:text-base ${s.color}`}>{s.value}</p>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Valuation Table */}
        <div className="overflow-x-auto">
          <table className="ui-table">
            <thead>
              <tr className="bg-slate-50/30">
                <th className="ui-th !py-3 w-10">#</th>
                <th className="ui-th !py-3 text-left w-56">Product Name</th>
                <th className="ui-th !py-3 text-left">Category</th>
                <th className="ui-th !py-3 text-right">Retail SP</th>
                <th className="ui-th !py-3 text-right">Casting CP</th>
                <th className="ui-th !py-3 text-center">Volume Sold</th>
                <th className="ui-th !py-3 text-right">Revenues</th>
                <th className="ui-th !py-3 text-right">Expenses (CP)</th>
                <th className="ui-th !py-3 text-right">Earnings</th>
                <th className="ui-th !py-3 text-center">Margin %</th>
                <th className="ui-th !py-3 text-center">Current Stock</th>
                <th className="ui-th !py-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {products.length === 0 && (
                <tr>
                  <td colSpan={12} className="ui-td text-center text-slate-400 py-12">
                    No products added in registry yet.
                  </td>
                </tr>
              )}
              {products.map((prod, i) => {
                const sp = Number(prod.price) || 0;
                const cp = Number(prod.castingPrice) || Number(prod.costing_price) || sp * 0.65;
                const stockQty = Number(prod.stock) || 0;
                const stockUnit = prod.stockUnit || prod.weight_unit || '';
                const category = prod.category || prod.category_name || '—';

                // Lookup sales
                const analytics = productData.find(pd => pd.name === prod.name || pd.name?.toLowerCase() === prod.name?.toLowerCase());
                const unitsSold = analytics?.unitsSold || 0;
                const sellRevenue = analytics?.sellingRevenue || 0;
                const costBills = analytics?.costRevenue || 0;
                const profit = analytics?.profit || 0;
                const margin = analytics?.margin || 0;

                // Storage alerts
                const isOut = stockQty === 0;
                const isLow = stockQty > 0 && stockQty < 40;
                
                const badgeStyle = isOut
                  ? 'bg-rose-50 text-rose-700 border-rose-100'
                  : isLow
                    ? 'bg-amber-50 text-amber-700 border-amber-100'
                    : 'bg-emerald-50 text-emerald-700 border-emerald-100';
                
                const badgeLabel = isOut ? 'Deficit' : isLow ? 'Low Stock' : 'In Stock';

                return (
                  <tr key={prod.id || i} className="hover:bg-emerald-50/10 transition-colors">
                    <td className="ui-td font-semibold text-slate-400">{i + 1}</td>
                    <td className="ui-td font-bold text-slate-800">
                      <div className="flex items-center gap-2">
                        <span className="text-base flex-shrink-0">{prod.image || '📦'}</span>
                        <div className="truncate max-w-[140px]">
                          <span className="block font-bold text-slate-800 leading-tight truncate">{prod.name}</span>
                          {prod.batchNumber && <span className="text-[10px] text-slate-400 font-medium block mt-0.5">Batch: {prod.batchNumber}</span>}
                        </div>
                      </div>
                    </td>
                    <td className="ui-td text-left">
                      <span className="inline-flex px-2 py-0.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100">
                        {category}
                      </span>
                    </td>
                    <td className="ui-td text-right text-emerald-600 font-extrabold">₹{sp.toLocaleString('en-IN')}</td>
                    <td className="ui-td text-right text-rose-600 font-bold">₹{Math.round(cp).toLocaleString('en-IN')}</td>
                    <td className="ui-td text-center text-slate-500 font-bold">
                      {unitsSold > 0 ? unitsSold : <span className="text-slate-200">—</span>}
                    </td>
                    <td className="ui-td text-right text-emerald-600 font-extrabold">
                      {sellRevenue > 0 ? `₹${fmt(sellRevenue)}` : <span className="text-slate-200">—</span>}
                    </td>
                    <td className="ui-td text-right text-rose-600 font-bold">
                      {costBills > 0 ? `₹${fmt(costBills)}` : <span className="text-slate-200">—</span>}
                    </td>
                    <td className="ui-td text-right text-purple-700 font-black">
                      {profit !== 0 ? `₹${fmt(profit)}` : <span className="text-slate-200">—</span>}
                    </td>
                    <td className="ui-td text-center">
                      {margin !== 0 ? (
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                          margin >= 40 
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                            : margin >= 25
                              ? 'bg-amber-50 text-amber-700 border-amber-100'
                              : 'bg-rose-50 text-rose-700 border-rose-100'
                        }`}>
                          {margin.toFixed(1)}%
                        </span>
                      ) : (
                        <span className="text-slate-200">—</span>
                      )}
                    </td>
                    <td className="ui-td text-center">
                      <span className={`font-black text-sm ${isOut ? 'text-rose-600' : isLow ? 'text-amber-600' : 'text-emerald-700'}`}>
                        {stockQty}
                      </span>
                      {stockQty > 0 && stockUnit && (
                        <span className="block text-[9px] text-slate-400 font-medium">{stockUnit}</span>
                      )}
                    </td>
                    <td className="ui-td text-center">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border ${badgeStyle}`}>
                        {badgeLabel}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Catalog summary tray */}
        {products.length > 0 && (
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-xs font-semibold">
            <span className="text-slate-500">
              Inventory status: <strong className="text-slate-700">{products.filter(p => (Number(p.stock) || 0) > 0).length} in stock</strong> · <strong className="text-slate-700">{products.filter(p => (Number(p.stock) || 0) === 0).length} empty shelf</strong>
            </span>
            <div className="flex flex-wrap gap-4">
              {[
                { label: 'Retail sales pool', val: productData.reduce((s, p) => s + p.sellingRevenue, 0), c: 'text-emerald-700', bullet: 'bg-emerald-500' },
                { label: 'Aggregate bills', val: productData.reduce((s, p) => s + p.costRevenue, 0), c: 'text-rose-700', bullet: 'bg-rose-500' },
                { label: 'Realized Earnings', val: productData.reduce((s, p) => s + p.profit, 0), c: 'text-purple-700', bullet: 'bg-purple-500' }
              ].map(x => (
                <span key={x.label} className={`flex items-center gap-1.5 font-bold ${x.c}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${x.bullet}`} />
                  {x.label}: ₹{fmt(x.val)}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* TOP PERFORMER BANNER */}
      {topProduct && (
        <div className="rounded-3xl border border-emerald-200/60 bg-gradient-to-r from-emerald-50 to-green-50 p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-sm">
          <div>
            <p className="text-emerald-700 text-[10px] font-bold uppercase tracking-wider mb-1 flex items-center gap-1">
              <Star size={12} className="fill-emerald-600 text-emerald-600" />
              Store Champion Product
            </p>
            <h3 className="text-slate-800 font-extrabold text-xl mb-1">{topProduct.name}</h3>
            <p className="text-slate-500 text-xs font-semibold">
              Volume: <strong className="text-slate-700">{topProduct.unitsSold} units</strong> &nbsp;·&nbsp; Selling Revenue: <strong className="text-emerald-600">₹{fmt(topProduct.sellingRevenue)}</strong> &nbsp;·&nbsp; Casting Bills: <strong className="text-rose-600">₹{fmt(topProduct.costRevenue)}</strong> &nbsp;·&nbsp; Margin: <strong className="text-purple-700">{topProduct.margin.toFixed(1)}%</strong>
            </p>
          </div>
          <button onClick={() => setSelProduct(topProduct)} className="ui-btn-primary flex items-center gap-2">
            <Eye size={15} />
            Show Invoices
          </button>
        </div>
      )}

      {/* REGIONAL / CITY DRILL DOWN DETAIL MODAL */}
      {selectedCity && (() => {
        const cityOrders = filteredOrders.filter(o => extractCity(o.address) === selectedCity);
        const byCustomer = {};
        cityOrders.forEach(o => {
          const k = o.customer || 'Guest';
          if (!byCustomer[k]) byCustomer[k] = { customer: k, address: o.address, orders: [] };
          byCustomer[k].orders.push(o);
        });
        const customers = Object.values(byCustomer);
        const cityTotalRev = cityOrders.reduce((s, o) => s + o.total, 0);

        const showInvoice = (order, custName) => {
          setCityInvoiceData({ order, custName });
        };

        return (
          <div className="ui-modal-overlay">
            <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl border border-slate-100 animate-fade-in">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-cyan-600 to-cyan-700 px-6 py-5 text-white flex justify-between items-center">
                <div>
                  <h2 className="font-extrabold text-lg flex items-center gap-1.5">📍 Zone: {selectedCity}</h2>
                  <p className="text-cyan-100 text-xs mt-1">
                    {customers.length} customer{customers.length !== 1 ? 's' : ''} · {cityOrders.length} order{cityOrders.length !== 1 ? 's' : ''} · ₹{fmt(cityTotalRev)} total
                  </p>
                </div>
                <button
                  onClick={() => setSelectedCity(null)}
                  className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 text-white border-transparent cursor-pointer flex items-center justify-center font-bold text-sm"
                >
                  ✕
                </button>
              </div>

              {/* Modal Body */}
              <div className="overflow-y-auto ui-scrollbar p-6 space-y-4 flex-1">
                {customers.map((cust, ci) => (
                  <div key={ci} className="border border-slate-150 rounded-2xl overflow-hidden bg-white">
                    {/* Customer Header */}
                    <div className="bg-slate-50/50 px-4 py-3 border-b border-slate-150 flex items-center gap-3 justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center text-white font-extrabold text-xs">
                          {cust.customer.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <span className="block font-bold text-slate-800 text-xs leading-tight">{cust.customer}</span>
                          <span className="text-[10px] text-slate-400 font-medium block mt-0.5">Address: {cust.address || selectedCity}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-black text-cyan-600 bg-cyan-50 border border-cyan-100 px-2 py-0.5 rounded-md">
                          ₹{fmt(cust.orders.reduce((s, o) => s + o.total, 0))}
                        </span>
                      </div>
                    </div>

                    {/* Order listings */}
                    <div className="divide-y divide-slate-100">
                      {cust.orders.map((order, oi) => (
                        <div key={oi} className="p-3.5 flex items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className="bg-blue-50 text-blue-700 font-extrabold text-[10px] px-2 py-0.5 rounded border border-blue-100">{order.id}</span>
                              <span className="text-[10px] text-slate-400 font-medium">{order.date} · {order.time}</span>
                            </div>
                            <p className="text-xs text-slate-500 font-medium line-clamp-1">
                              {(order.products || []).map(p => `${p.name} × ${p.quantity}`).join(' · ') || 'No items'}
                            </p>
                          </div>
                          
                          <div className="text-right flex-shrink-0 flex flex-col items-end gap-1.5">
                            <span className="font-extrabold text-slate-800 text-sm">₹{fmt(order.total)}</span>
                            <button
                              onClick={() => showInvoice(order, cust.customer)}
                              className="ui-btn-primary !px-2.5 !py-1 !rounded-md text-[10px] flex items-center gap-1 shadow-emerald-100"
                            >
                              <FileText size={10} />
                              Invoice
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-slate-100 flex justify-end bg-slate-50">
                <button
                  onClick={() => setSelectedCity(null)}
                  className="ui-btn-secondary !px-4 !py-2 !rounded-xl text-xs"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* REGIONAL / DRILL DOWN SPECIFIC INVOICE MODAL */}
      {cityInvoiceData && (() => {
        const { order, custName } = cityInvoiceData;
        const invNum = `INV-${new Date().getFullYear()}-${String(order.id || '').replace(/\D/g, '').padStart(4, '0') || Math.floor(Math.random() * 9000 + 1000)}`;
        const items = order.products || [];
        const subtotal = order.total || 0;
        const gstRate = 0;
        const gstAmt = subtotal * gstRate;
        const grandTotal = subtotal + gstAmt;

        const doPrint = () => {
          const w = window.open('', '_blank', 'width=820,height=950');
          w.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Invoice ${invNum}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:'Inter',sans-serif;background:#f1f5f9;padding:30px;color:#1e293b}
    .page{background:#fff;max-width:720px;margin:0 auto;border-radius:16px;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,0.12)}
    .header{background:linear-gradient(135deg,#14532d 0%,#16a34a 60%,#22c55e 100%);padding:36px 40px;color:#fff}
    .header-top{display:flex;justify-content:space-between;align-items:flex-start}
    .brand{display:flex;align-items:center;gap:12px}
    .brand-icon{width:52px;height:52px;background:rgba(255,255,255,0.2);border-radius:14px;display:flex;align-items:center;justify-content:center;font-size:26px}
    .brand-name{font-size:24px;font-weight:900;letter-spacing:-0.5px}
    .brand-sub{font-size:12px;opacity:0.8;margin-top:3px;letter-spacing:1px}
    .inv-badge{background:rgba(255,255,255,0.2);border-radius:10px;padding:10px 18px;text-align:right}
    .inv-badge .label{font-size:10px;opacity:0.7;text-transform:uppercase;letter-spacing:1.5px}
    .inv-badge .num{font-size:20px;font-weight:800;margin-top:2px}
    .header-meta{display:flex;gap:32px;margin-top:24px;padding-top:20px;border-top:1px solid rgba(255,255,255,0.2)}
    .meta-item .label{font-size:10px;opacity:0.7;text-transform:uppercase;letter-spacing:1px}
    .meta-item .val{font-size:14px;font-weight:700;margin-top:3px}
    .body{padding:36px 40px}
    .two-col{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-bottom:32px}
    .info-box{background:#f8fafc;border-radius:12px;padding:18px}
    .info-box h4{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#94a3b8;margin-bottom:10px}
    .info-box p{font-size:13px;color:#374151;line-height:1.7}
    .info-box .main{font-size:15px;font-weight:700;color:#111827}
    table{width:100%;border-collapse:collapse;margin-bottom:28px}
    thead tr{background:linear-gradient(135deg,#f0fdf4,#dcfce7)}
    th{padding:12px 14px;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#15803d}
    th:last-child,td:last-child{text-align:right}
    td{padding:13px 14px;font-size:13px;color:#374151;border-bottom:1px solid #f1f5f9}
    tr:last-child td{border-bottom:none}
    tbody tr:hover{background:#fafffe}
    .item-name{font-weight:600;color:#111827}
    .item-sub{font-size:11px;color:#94a3b8;margin-top:3px}
    .totals{margin-left:auto;width:260px}
    .total-row{display:flex;justify-content:space-between;padding:7px 0;font-size:13px;color:#64748b}
    .total-row.grand{border-top:2px solid #16a34a;margin-top:6px;padding-top:12px;font-size:17px;font-weight:800;color:#111827}
    .total-row.grand .amount{color:#16a34a}
    .footer{background:#f8fafc;border-top:1px solid #e2e8f0;padding:24px 40px;display:flex;justify-content:space-between;align-items:center}
    .footer-left{font-size:12px;color:#64748b;line-height:1.8}
    .thanks{font-size:13px;font-weight:700;color:#16a34a}
    .watermark{text-align:center;padding:16px;font-size:11px;color:#cbd5e1}
    @media print{body{background:#fff;padding:0}.page{box-shadow:none;border-radius:0}}
  </style>
</head>
<body>
<div class="page">
  <div class="header">
    <div class="header-top">
      <div class="brand">
        <div class="brand-icon">🌿</div>
        <div>
          <div class="brand-name">Manthuli Organic Shop</div>
          <div class="brand-sub">Fresh • Organic • Natural</div>
        </div>
      </div>
      <div class="inv-badge"><div class="label">Invoice</div><div class="num">${invNum}</div></div>
    </div>
    <div class="header-meta">
      <div class="meta-item"><div class="label">Invoice Date</div><div class="val">${order.date || new Date().toLocaleDateString('en-GB').replaceAll('/', '.')}</div></div>
      <div class="meta-item"><div class="label">Order Ref</div><div class="val">${order.id || '—'}</div></div>
      <div class="meta-item"><div class="label">City</div><div class="val">📍 ${selectedCity || ''}</div></div>
    </div>
  </div>
  <div class="body">
    <div class="two-col">
      <div class="info-box"><h4>Bill To</h4><p class="main">${custName}</p><p style="margin-top:6px;color:#64748b;font-size:12px">${order.address || ''}</p></div>
      <div class="info-box"><h4>From</h4><p class="main">Manthuli Organic Shop</p><p>📞 +91-9876543210</p><p>📧 info@manthuliorganic.com</p><p style="margin-top:6px;color:#64748b;font-size:12px">123 Organic Lane, Green City</p></div>
    </div>
    <table>
      <thead><tr><th>#</th><th>Item Description</th><th>Weight / Unit</th><th>Qty</th><th>Unit Price</th><th>Amount</th></tr></thead>
      <tbody>
        ${items.map((it, ii) => `<tr><td style="color:#94a3b8;font-weight:600">${String(ii + 1).padStart(2, '0')}</td><td><div class="item-name">${it.name}</div><div class="item-sub">Organic · Premium Quality</div></td><td>${it.weight || '—'} ${it.unit || ''}</td><td style="text-align:center">${it.quantity || 1}</td><td>₹${(it.price || 0).toLocaleString('en-IN')}</td><td style="font-weight:700;color:#111827">₹${((it.price || 0) * (it.quantity || 1)).toLocaleString('en-IN')}</td></tr>`).join('')}
      </tbody>
    </table>
    <div class="totals">
      <div class="total-row"><span>Subtotal</span><span>₹${subtotal.toLocaleString('en-IN')}</span></div>
      <div class="total-row"><span>GST (${(gstRate * 100).toFixed(0)}%)</span><span>₹${gstAmt.toLocaleString('en-IN')}</span></div>
      <div class="total-row"><span>Delivery</span><span style="color:#16a34a;font-weight:700">FREE</span></div>
      <div class="total-row grand"><span>Grand Total</span><span class="amount">₹${grandTotal.toLocaleString('en-IN')}</span></div>
    </div>
  </div>
  <div class="footer">
    <div class="footer-left"><strong>Payment:</strong> Cash on Delivery<br/>GSTIN: 29AAACH7409R1ZZ</div>
    <div class="thanks">Thank you! 🌿<span>We appreciate your business</span></div>
  </div>
  <div class="watermark">Computer-generated invoice · No signature required</div>
</div>
<script>setTimeout(()=>window.print(),400)</script>
</body>
</html>`);
          w.document.close();
        };

        return (
          <div className="ui-modal-overlay">
            <div className="bg-white rounded-3xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl border border-slate-100 animate-fade-in">
              {/* Header */}
              <div className="bg-gradient-to-r from-emerald-800 to-emerald-600 px-6 py-5 text-white flex justify-between items-center">
                <div className="flex items-center gap-2.5">
                  <span className="text-xl">🌿</span>
                  <div>
                    <h4 className="font-extrabold text-sm leading-tight">Manthuli Organic Shop</h4>
                    <span className="text-[10px] text-emerald-100 font-bold uppercase tracking-wider block">Receipt Portal</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button onClick={doPrint} className="px-3.5 py-1.5 rounded-lg text-xs font-bold bg-white/25 border border-white/40 text-white hover:bg-white/35 transition-colors">
                    Print Invoice
                  </button>
                  <button
                    onClick={() => setCityInvoiceData(null)}
                    className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 text-white border-transparent cursor-pointer flex items-center justify-center font-bold text-sm"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Invoice body preview */}
              <div className="overflow-y-auto ui-scrollbar p-6 space-y-5 flex-1">
                {/* Metadatas */}
                <div className="grid grid-cols-3 gap-2 py-3 bg-slate-50 border border-slate-100 rounded-xl text-center">
                  <div>
                    <span className="text-[8px] font-bold text-slate-400 block uppercase">Reference ID</span>
                    <span className="text-xs font-black text-slate-700">{order.id}</span>
                  </div>
                  <div>
                    <span className="text-[8px] font-bold text-slate-400 block uppercase">Receipt Date</span>
                    <span className="text-xs font-bold text-slate-600">{order.date}</span>
                  </div>
                  <div>
                    <span className="text-[8px] font-bold text-slate-400 block uppercase">Tax Number</span>
                    <span className="text-xs font-bold text-slate-600">{invNum}</span>
                  </div>
                </div>

                {/* Billing specs */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50/50 border border-slate-100 rounded-xl p-3">
                    <span className="text-[8px] font-bold text-slate-400 block uppercase mb-1">Deliver To</span>
                    <strong className="text-xs text-slate-800 block mb-0.5">{custName}</strong>
                    <span className="text-[10px] text-slate-500 leading-relaxed block">{order.address}</span>
                  </div>
                  <div className="bg-slate-50/50 border border-slate-100 rounded-xl p-3">
                    <span className="text-[8px] font-bold text-slate-400 block uppercase mb-1">Supplier</span>
                    <strong className="text-xs text-slate-800 block mb-0.5">Manthuli Organic Shop</strong>
                    <span className="text-[10px] text-slate-500 leading-relaxed block">📞 +91-9876543210<br/>📧 info@manthuliorganic.com</span>
                  </div>
                </div>

                {/* Items */}
                <div className="ui-table-wrap border border-slate-100">
                  <table className="ui-table">
                    <thead>
                      <tr className="bg-slate-50/20">
                        <th className="ui-th !py-2 w-8">#</th>
                        <th className="ui-th !py-2 text-left">Item Name</th>
                        <th className="ui-th !py-2 text-center">Weight</th>
                        <th className="ui-th !py-2 text-center">Qty</th>
                        <th className="ui-th !py-2 text-right">Price</th>
                        <th className="ui-th !py-2 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50 text-xs">
                      {items.map((it, ii) => (
                        <tr key={ii} className="hover:bg-slate-50/30">
                          <td className="ui-td text-slate-400 text-center font-bold">{ii + 1}</td>
                          <td className="ui-td font-bold text-slate-800">
                            {it.name}
                            <span className="block text-[8px] text-slate-400 font-medium mt-0.5">Premium Organic Pack</span>
                          </td>
                          <td className="ui-td text-center text-slate-500 font-medium">{it.weight || '—'} {it.unit || ''}</td>
                          <td className="ui-td text-center text-slate-700 font-black">{it.quantity || 1}</td>
                          <td className="ui-td text-right text-slate-600">₹{(it.price || 0).toLocaleString()}</td>
                          <td className="ui-td text-right font-extrabold text-slate-800">₹{((it.price || 0) * (it.quantity || 1)).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Totals */}
                <div className="flex justify-end pt-2">
                  <div className="w-56 space-y-1 text-xs">
                    <div className="flex justify-between text-slate-500">
                      <span>Subtotal</span>
                      <span className="font-semibold text-slate-700">₹{subtotal.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-slate-500">
                      <span>GST (0%)</span>
                      <span className="font-semibold text-slate-700">₹{gstAmt.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-slate-500">
                      <span>Delivery Fee</span>
                      <span className="font-bold text-emerald-600">FREE</span>
                    </div>
                    <div className="border-t border-emerald-500/35 pt-2 mt-2 flex justify-between items-center">
                      <span className="text-sm font-black text-emerald-700">Grand Total</span>
                      <span className="text-base font-black text-emerald-600">₹{grandTotal.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* DETAILED DRILL OVERLAYS */}
      {selProduct && (
        <ProductDetailModal
          product={selProduct}
          allOrders={filteredOrders}
          onClose={() => setSelProduct(null)}
          onViewInvoice={o => setInvoiceOrder(o)}
        />
      )}
      {invoiceOrder && (
        <InvoiceModal
          order={invoiceOrder}
          onClose={() => setInvoiceOrder(null)}
        />
      )}
    </div>
  );
};

export default Analytics;